import React, { useEffect, useRef, useState } from "react";
import type { Keys, GameState, Building } from "./types";
import {
  WORLD_W,
  WORLD_H,
  TILE_SIZE,
  MAX_SPEED,
  ACCEL,
  BRAKE,
  FRICTION,
  TURN_RATE,
  CAR_W,
  CAR_H,
  INITIAL_CAR_X,
  INITIAL_CAR_Y,
  BUILDINGS,
  CAMERA_ZOOM,
  STARTING_LINE_INDEX,
  STARTING_LINE_POSITION,
} from "./constants";
import {
  drawGrassTile,
  drawTree,
  drawRock,
  drawDecorativeGrass,
  drawFlower,
  drawCar,
  drawBuilding,
  drawPath,
} from "./utils/drawing";
import { generatePaths } from "./utils/paths";
import {
  checkBuildingCollision,
  checkPointCollision,
} from "./utils/collision";
import {
  generateTrees,
  generateRocks,
  generateDecorativeGrass,
  generateFlowers,
} from "./utils/generation";
import {
  initializeSnowflakes,
  updateSnowflake,
  getSnowflakeOpacity,
  drawSnowflake,
} from "./utils/snow";
import { drawFlagCounter } from "./utils/ui";
import {
  ANIMATION_DURATION,
  MAX_WIGGLE_ANGLE,
} from "./constants";
import BuildingModal from "./components/BuildingModal";
import Joystick from "./components/Joystick";
import teslaImage from "./components/tesla.png";

export default function Car() {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  const keysRef = useRef<Keys>({
    ArrowUp: false,
    ArrowDown: false,
    ArrowLeft: false,
    ArrowRight: false,
  });

  const stateRef = useRef<GameState>({
    x: INITIAL_CAR_X,
    y: INITIAL_CAR_Y,
    angle: 0,
    speed: 0,
    lastT: performance.now(),
  });

  const [modalOpen, setModalOpen] = useState(false);
  const [selectedBuildingIndex, setSelectedBuildingIndex] = useState<number | null>(null);
  const [visitedFlags, setVisitedFlags] = useState<Set<number>>(new Set());
  const visitedFlagsRef = useRef<Set<number>>(new Set());
  const modalOpenRef = useRef(false);
  const lastTouchedBuildingRef = useRef<Building | null>(null);
  const carImageRef = useRef<HTMLImageElement | null>(null);
  
  // Joystick state
  const joystickRef = useRef<{ x: number; y: number }>({ x: 0, y: 0 });

  // Sync refs with state
  useEffect(() => {
    modalOpenRef.current = modalOpen;
  }, [modalOpen]);

  useEffect(() => {
    visitedFlagsRef.current = visitedFlags;
  }, [visitedFlags]);

  // Load car image
  useEffect(() => {
    const img = new Image();
    img.src = teslaImage;
    img.onload = () => {
      carImageRef.current = img;
    };
    img.onerror = () => {
      console.warn("Car image failed to load. Using fallback drawing.");
    };
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    // Set canvas to fullscreen
    const resizeCanvas = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resizeCanvas();
    window.addEventListener("resize", resizeCanvas);

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key in keysRef.current) {
        keysRef.current[e.key as keyof Keys] = true;
        e.preventDefault();
      }
    };

    const onKeyUp = (e: KeyboardEvent) => {
      if (e.key in keysRef.current) {
        keysRef.current[e.key as keyof Keys] = false;
        e.preventDefault();
      }
    };

    window.addEventListener("keydown", onKeyDown);
    window.addEventListener("keyup", onKeyUp);

    let CANVAS_W = canvas.width;
    let CANVAS_H = canvas.height;

    // Generate smooth dirt paths between all buildings (needed for tree placement)
    // Include starting line position at the beginning, connecting to first flag
    const allBuildingsForPaths = [STARTING_LINE_POSITION, ...BUILDINGS];
    const paths = generatePaths(allBuildingsForPaths);

    // Generate all objects
    const trees = generateTrees(paths);
    const rocks = generateRocks(paths);
    const decorativeGrass = generateDecorativeGrass(paths);
    const flowers = generateFlowers(paths);

    // Camera position (will follow car)
    let cameraX = 0;
    let cameraY = 0;

    let rafId = 0;

    const clamp = (v: number, lo: number, hi: number) =>
      Math.max(lo, Math.min(hi, v));

    // Track animation state for trees and grass (timestamp when hit)
    const treeAnimations = new Map<number, number>(); // tree index -> hit timestamp
    const grassAnimations = new Map<number, number>(); // grass index -> hit timestamp

    // Initialize snowflakes in world space
    const snowflakes = initializeSnowflakes();

    const loop = (now: number) => {
      // Update canvas dimensions if resized
      CANVAS_W = canvas.width;
      CANVAS_H = canvas.height;

      const s = stateRef.current;
      const dt = Math.min(0.033, (now - s.lastT) / 1000);
      s.lastT = now;

      const keys = keysRef.current;
      const joystick = joystickRef.current;

      // Combine keyboard and joystick input
      const throttle = keys.ArrowUp ? 1 : (joystick.y > 0 ? joystick.y : 0);
      const brake = keys.ArrowDown ? 1 : (joystick.y < 0 ? -joystick.y : 0);
      const steerLeft = keys.ArrowLeft ? 1 : (joystick.x < 0 ? -joystick.x : 0);
      const steerRight = keys.ArrowRight ? 1 : (joystick.x > 0 ? joystick.x : 0);

      // Apply throttle/brake
      if (throttle > 0) {
        s.speed += ACCEL * dt * throttle;
      } else if (brake > 0) {
        s.speed -= BRAKE * dt * brake;
      } else {
        const sign = Math.sign(s.speed);
        const mag = Math.abs(s.speed);
        s.speed = Math.max(0, mag - FRICTION * dt) * sign;
      }

      s.speed = clamp(s.speed, -MAX_SPEED * 0.5, MAX_SPEED);

      const speedFactor = Math.min(1, Math.abs(s.speed) / MAX_SPEED);
      const turn = TURN_RATE * speedFactor;

      // Apply steering
      if (steerLeft > 0) {
        s.angle -= turn * dt * steerLeft * (s.speed >= 0 ? 1 : -1);
      }
      if (steerRight > 0) {
        s.angle += turn * dt * steerRight * (s.speed >= 0 ? 1 : -1);
      }

      s.x += Math.sin(s.angle) * s.speed * dt;
      s.y -= Math.cos(s.angle) * s.speed * dt;

      // World bounds
      s.x = clamp(s.x, 10, WORLD_W - 10);
      s.y = clamp(s.y, 10, WORLD_H - 10);

      // Check collision with buildings
      if (!modalOpenRef.current) {
        // First, check if car has moved away from the last touched building
        if (lastTouchedBuildingRef.current) {
          const stillTouching = checkBuildingCollision(
            s,
            lastTouchedBuildingRef.current
          );

          if (!stillTouching) {
            // Car has moved away, reset the last touched building
            lastTouchedBuildingRef.current = null;
          }
        }

        // Only check for new collisions if not touching the last building
        if (!lastTouchedBuildingRef.current) {
          // Check starting line flag first
          if (checkBuildingCollision(s, STARTING_LINE_POSITION)) {
            setSelectedBuildingIndex(STARTING_LINE_INDEX);
            setModalOpen(true);
            lastTouchedBuildingRef.current = STARTING_LINE_POSITION;
            // Mark starting line as visited
            const newVisited = new Set(visitedFlagsRef.current);
            newVisited.add(STARTING_LINE_INDEX);
            setVisitedFlags(newVisited);
            visitedFlagsRef.current = newVisited;
          } else {
            // Check regular flags
            for (let i = 0; i < BUILDINGS.length; i++) {
              const building = BUILDINGS[i];
              if (checkBuildingCollision(s, building)) {
                setSelectedBuildingIndex(i);
                setModalOpen(true);
                lastTouchedBuildingRef.current = building;
                // Mark this flag as visited
                const newVisited = new Set(visitedFlagsRef.current);
                newVisited.add(i);
                setVisitedFlags(newVisited);
                visitedFlagsRef.current = newVisited;
                break;
              }
            }
          }
        }
      }

      // Check collisions with trees and grass for animation
      const carW = CAR_W;
      const carH = CAR_H;
      
      trees.forEach((tree, index) => {
        if (checkPointCollision(tree.x, tree.y, 80, s.x, s.y, carW, carH)) {
          if (!treeAnimations.has(index)) {
            treeAnimations.set(index, now);
          }
        }
      });

      decorativeGrass.forEach((grass, index) => {
        if (checkPointCollision(grass.x, grass.y, 60, s.x, s.y, carW, carH)) {
          if (!grassAnimations.has(index)) {
            grassAnimations.set(index, now);
          }
        }
      });

      // Clean up old animations
      treeAnimations.forEach((timestamp, index) => {
        if (now - timestamp > ANIMATION_DURATION) {
          treeAnimations.delete(index);
        }
      });
      grassAnimations.forEach((timestamp, index) => {
        if (now - timestamp > ANIMATION_DURATION) {
          grassAnimations.delete(index);
        }
      });

      // Update camera to follow car (centered on car)
      // Apply zoom: show more area by scaling the viewport
      const viewportW = CANVAS_W / CAMERA_ZOOM;
      const viewportH = CANVAS_H / CAMERA_ZOOM;
      
      cameraX = s.x - viewportW / 2;
      cameraY = s.y - viewportH / 2;

      // Clamp camera to world bounds
      cameraX = clamp(cameraX, 0, WORLD_W - viewportW);
      cameraY = clamp(cameraY, 0, WORLD_H - viewportH);

      // Render
      ctx.clearRect(0, 0, CANVAS_W, CANVAS_H);

      // Draw grass tiles (Pokemon-style background)
      // Use zoomed viewport for tile calculations
      const startTileX = Math.floor(cameraX / TILE_SIZE);
      const startTileY = Math.floor(cameraY / TILE_SIZE);
      const endTileX = Math.ceil((cameraX + viewportW) / TILE_SIZE);
      const endTileY = Math.ceil((cameraY + viewportH) / TILE_SIZE);

      // Draw tiles
      for (let ty = startTileY; ty <= endTileY; ty++) {
        for (let tx = startTileX; tx <= endTileX; tx++) {
          const worldX = tx * TILE_SIZE;
          const worldY = ty * TILE_SIZE;
          // Apply zoom when converting to screen coordinates
          const screenX = (worldX - cameraX) * CAMERA_ZOOM;
          const screenY = (worldY - cameraY) * CAMERA_ZOOM;

          // Draw grass tile
          const variant = (tx + ty * 7) % 4;
          drawGrassTile(ctx, screenX, screenY, variant);
        }
      }

      // Draw smooth dirt paths
      paths.forEach((segment) => {
        drawPath(ctx, segment, cameraX, cameraY, CAMERA_ZOOM);
      });

      // Draw decorative grass relative to camera (draw first, smallest)
      decorativeGrass.forEach((grass, index) => {
        const screenX = (grass.x - cameraX) * CAMERA_ZOOM;
        const screenY = (grass.y - cameraY) * CAMERA_ZOOM;
        // Only draw if visible on screen (larger bounds for bigger grass)
        if (
          screenX > -30 &&
          screenX < CANVAS_W + 30 &&
          screenY > -30 &&
          screenY < CANVAS_H + 30
        ) {
          // Calculate wiggle animation
          let wiggleAngle = 0;
          if (grassAnimations.has(index)) {
            const hitTime = grassAnimations.get(index)!;
            const elapsed = now - hitTime;
            const progress = Math.min(elapsed / ANIMATION_DURATION, 1);
            // Slow wiggle that fades out
            const fadeOut = 1 - progress;
            wiggleAngle = Math.sin(elapsed * 0.003) * MAX_WIGGLE_ANGLE * fadeOut;
          }
          drawDecorativeGrass(ctx, screenX, screenY, CAMERA_ZOOM, wiggleAngle);
        }
      });

      // Draw flowers relative to camera (draw before rocks, smaller)
      flowers.forEach((flower) => {
        const screenX = (flower.x - cameraX) * CAMERA_ZOOM;
        const screenY = (flower.y - cameraY) * CAMERA_ZOOM;
        // Only draw if visible on screen
        if (
          screenX > -20 &&
          screenX < CANVAS_W + 20 &&
          screenY > -20 &&
          screenY < CANVAS_H + 20
        ) {
          drawFlower(ctx, screenX, screenY, flower.type, CAMERA_ZOOM);
        }
      });

      // Draw rocks relative to camera
      rocks.forEach((rock) => {
        const screenX = (rock.x - cameraX) * CAMERA_ZOOM;
        const screenY = (rock.y - cameraY) * CAMERA_ZOOM;
        // Only draw if visible on screen (larger bounds for bigger rocks)
        if (
          screenX > -30 &&
          screenX < CANVAS_W + 30 &&
          screenY > -30 &&
          screenY < CANVAS_H + 30
        ) {
          drawRock(ctx, screenX, screenY, rock.type, CAMERA_ZOOM);
        }
      });

      // Draw trees relative to camera
      trees.forEach((tree, index) => {
        const screenX = (tree.x - cameraX) * CAMERA_ZOOM;
        const screenY = (tree.y - cameraY) * CAMERA_ZOOM;
        // Only draw if tree is visible on screen
        if (
          screenX > -50 &&
          screenX < CANVAS_W + 50 &&
          screenY > -50 &&
          screenY < CANVAS_H + 50
        ) {
          // Calculate wiggle animation
          let wiggleAngle = 0;
          if (treeAnimations.has(index)) {
            const hitTime = treeAnimations.get(index)!;
            const elapsed = now - hitTime;
            const progress = Math.min(elapsed / ANIMATION_DURATION, 1);
            // Slow wiggle that fades out
            const fadeOut = 1 - progress;
            wiggleAngle = Math.sin(elapsed * 0.003) * MAX_WIGGLE_ANGLE * fadeOut;
          }
          drawTree(ctx, screenX, screenY, tree.type, CAMERA_ZOOM, wiggleAngle);
        }
      });

      // Draw starting line flag first (before regular flags)
      const startingLineScreenX = (STARTING_LINE_POSITION.x - cameraX) * CAMERA_ZOOM;
      const startingLineScreenY = (STARTING_LINE_POSITION.y - cameraY) * CAMERA_ZOOM;
      if (
        startingLineScreenX > -100 &&
        startingLineScreenX < CANVAS_W + 100 &&
        startingLineScreenY > -200 &&
        startingLineScreenY < CANVAS_H + 100
      ) {
        drawBuilding(ctx, startingLineScreenX, startingLineScreenY, STARTING_LINE_INDEX, CAMERA_ZOOM);
      }

      // Draw buildings (flags) relative to camera - DRAW LAST so they're on top of everything
      BUILDINGS.forEach((building, index) => {
        const screenX = (building.x - cameraX) * CAMERA_ZOOM;
        const screenY = (building.y - cameraY) * CAMERA_ZOOM;
        // Only draw if building is visible on screen (use zoomed viewport)
        if (
          screenX > -100 &&
          screenX < CANVAS_W + 100 &&
          screenY > -200 &&
          screenY < CANVAS_H + 100
        ) {
          drawBuilding(ctx, screenX, screenY, index, CAMERA_ZOOM);
        }
      });

      // Draw car relative to camera
      const carScreenX = (s.x - cameraX) * CAMERA_ZOOM;
      const carScreenY = (s.y - cameraY) * CAMERA_ZOOM;
      drawCar(ctx, carScreenX, carScreenY, s.angle, CAR_W * CAMERA_ZOOM, CAR_H * CAMERA_ZOOM, carImageRef.current);

      // Update and draw snowflakes (in world space)
      snowflakes.forEach((flake) => {
        updateSnowflake(flake, dt, now);
        
        // Convert to screen coordinates
        const screenX = (flake.x - cameraX) * CAMERA_ZOOM;
        const screenY = (flake.y - cameraY) * CAMERA_ZOOM;
        
        // Only draw if visible on screen
        if (
          screenX > -20 &&
          screenX < CANVAS_W + 20 &&
          screenY > -20 &&
          screenY < CANVAS_H + 20
        ) {
          const displayOpacity = getSnowflakeOpacity(flake, now);
          drawSnowflake(ctx, flake, screenX, screenY, displayOpacity);
        }
      });

      // Draw flag counter at top right
      drawFlagCounter(ctx, visitedFlagsRef.current.size, CANVAS_W, CANVAS_H);

      rafId = requestAnimationFrame(loop);
    };

    rafId = requestAnimationFrame(loop);

    return () => {
      cancelAnimationFrame(rafId);
      window.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("keyup", onKeyUp);
      window.removeEventListener("resize", resizeCanvas);
    };
  }, []);

  return (
    <>
    <canvas
      ref={canvasRef}
        style={{
          display: "block",
          width: "100vw",
          height: "100vh",
          margin: 0,
          padding: 0,
          border: "none",
          outline: "none",
          position: "fixed",
          top: 0,
          left: 0,
          background: "#000000",
        }}
      />
      <BuildingModal
        isOpen={modalOpen}
        buildingIndex={selectedBuildingIndex}
        onClose={() => setModalOpen(false)}
      />
      <Joystick
        onMove={(x, y) => {
          joystickRef.current = { x, y };
        }}
        onStop={() => {
          joystickRef.current = { x: 0, y: 0 };
        }}
      />
    </>
  );
}
